import React, { useState, useRef } from 'react';
import { Play, Download, ChevronDown, ChevronUp, Film, Maximize2 } from 'lucide-react';

/**
 * VideoAttachment - Video player component for video messages
 * Inspired by Rocket.Chat's VideoAttachment
 */
const VideoAttachment = ({
  src,
  filename,
  fileSize,
  mimeType,
  onDownload,
  collapsed: initialCollapsed = false,
  thumbnail
}) => {
  const [collapsed, setCollapsed] = useState(initialCollapsed);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const videoRef = useRef(null);
  const containerRef = useRef(null);

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Handle download
  const handleDownload = (e) => {
    e.stopPropagation();
    if (onDownload) {
      onDownload();
    } else if (src) {
      const link = document.createElement('a');
      link.href = src;
      link.download = filename || 'video';
      link.click();
    }
  };

  // Handle fullscreen
  const handleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      } else if (videoRef.current.webkitRequestFullscreen) {
        videoRef.current.webkitRequestFullscreen();
      }
    }
  };

  // Handle video error
  const handleError = () => {
    setError('Failed to load video');
  };

  return (
    <div
      ref={containerRef}
      className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden"
      style={{ maxWidth: '368px' }}
    >
      {/* Header */}
      <div
        className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800/50 cursor-pointer"
        onClick={() => setCollapsed(!collapsed)}
      >
        <Film className="w-4 h-4 text-slate-500" />
        <span className="flex-1 text-sm font-medium text-slate-700 dark:text-slate-300 truncate">
          {filename || 'Video message'}
        </span>
        {fileSize && (
          <span className="text-xs text-slate-500">
            {formatFileSize(fileSize)}
          </span>
        )}
        <button
          type="button"
          onClick={handleDownload}
          className="p-1 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          title="Download"
        >
          <Download className="w-4 h-4" />
        </button>
        {collapsed ? (
          <ChevronDown className="w-4 h-4 text-slate-400" />
        ) : (
          <ChevronUp className="w-4 h-4 text-slate-400" />
        )}
      </div>

      {/* Video Player */}
      {!collapsed && (
        <div className="relative bg-black">
          {error ? (
            <div className="aspect-video flex items-center justify-center">
              <p className="text-sm text-red-500">{error}</p>
            </div>
          ) : (
            <>
              <video
                ref={videoRef}
                src={src}
                controls
                preload="metadata"
                className="w-full aspect-video"
                onError={handleError}
                poster={thumbnail}
              >
                <source src={src} type={mimeType || 'video/webm'} />
                Your browser does not support the video tag.
              </video>

              {/* Fullscreen button overlay */}
              <button
                type="button"
                onClick={handleFullscreen}
                className="absolute top-2 right-2 p-1.5 bg-black/50 rounded text-white hover:bg-black/70 transition-colors"
                title="Fullscreen"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoAttachment;
